import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { oceanNoiseGLSL, getHeightGLSL } from './shaders/oceanNoise.glsl.js';

function generateWaterNormalMap(size) {
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = x / size;
      const v = y / size;
      const TAU = Math.PI * 2;

      // Layered sine waves for tileable ripples
      let dx = 0, dy = 0;
      dx += Math.cos(u * TAU * 4 + v * TAU * 3) * 0.3;
      dy += Math.sin(v * TAU * 4 + u * TAU * 2) * 0.3;
      dx += Math.cos(u * TAU * 7 - v * TAU * 5) * 0.2;
      dy += Math.sin(v * TAU * 6 + u * TAU * 8) * 0.2;
      dx += Math.cos(u * TAU * 13 + v * TAU * 11) * 0.1;
      dy += Math.sin(v * TAU * 12 - u * TAU * 9) * 0.1;

      const idx = (y * size + x) * 4;
      data[idx]     = Math.floor((dx * 0.5 + 0.5) * 255); // R = normal X
      data[idx + 1] = Math.floor((dy * 0.5 + 0.5) * 255); // G = normal Y
      data[idx + 2] = 255;                                  // B = normal Z (up)
      data[idx + 3] = 255;
    }
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.needsUpdate = true;
  return tex;
}

const oceanVertexShader = `
uniform float uTime;
uniform float uSeaSpeed;
uniform float uSeaFreq;
uniform float uSeaHeight;
uniform float uSeaChoppy;
varying vec3 vWorldPosition;

const int ITER_GEOMETRY = 3;

${oceanNoiseGLSL}
${getHeightGLSL(3)}

void main() {
    vec3 worldPos = (modelMatrix * vec4(position, 1.0)).xyz;
    worldPos.y += getHeight(worldPos.xz);
    vWorldPosition = worldPos;
    gl_Position = projectionMatrix * viewMatrix * vec4(worldPos, 1.0);
}
`;

const oceanFragmentShader = `
uniform float uTime;
uniform float uSeaSpeed;
uniform float uSeaFreq;
uniform float uSeaHeight;
uniform float uSeaChoppy;
uniform vec3 uSunDirection;
uniform sampler2D uNormalMap;
uniform float uNormalStrength;
uniform float uSSSIntensity;
uniform float uFoamAmount;
varying vec3 vWorldPosition;

const int ITER_FRAGMENT = 5;
const float SKY_INTENSITY = 1.0;
const vec3 SEA_BASE = vec3(0.1, 0.19, 0.22);
const vec3 SEA_WATER_COLOR = vec3(0.8, 0.9, 0.6);

${oceanNoiseGLSL}

float map_detailed(vec3 p) {
    float SEA_TIME = uTime * uSeaSpeed;
    float freq = uSeaFreq;
    float amp = uSeaHeight;
    float choppy = uSeaChoppy;
    vec2 uv = p.xz;
    uv.x *= 0.75;
    mat2 m = mat2(1.6, 1.2, -1.2, 1.6);

    float d, h = 0.0;
    for(int i = 0; i < ITER_FRAGMENT; i++) {
        d = sea_octave((uv + SEA_TIME) * freq, choppy);
        d += sea_octave((uv - SEA_TIME) * freq, choppy);
        h += d * amp;
        uv *= m;
        freq *= 1.9;
        amp *= 0.22;
        choppy = mix(choppy, 1.0, 0.2);
    }
    return p.y - h;
}

vec3 getNormal(vec3 p, float eps) {
    vec3 n;
    n.y = map_detailed(p);
    n.x = map_detailed(vec3(p.x + eps, p.y, p.z)) - n.y;
    n.z = map_detailed(vec3(p.x, p.y, p.z + eps)) - n.y;
    n.y = eps;
    return normalize(n);
}

vec3 sky_color(vec3 e) {
    e.y = max(e.y, 0.0);
    vec3 ret;
    ret.x = pow(1.0 - e.y, 2.0);
    ret.y = 1.0 - e.y;
    ret.z = 0.6 + (1.0 - e.y) * 0.4;
    return ret * SKY_INTENSITY;
}

float diffuse(vec3 n, vec3 l, float p) {
    return pow(dot(n, l) * 0.4 + 0.6, p);
}

float specular(vec3 n, vec3 l, vec3 e, float s) {
    float nrm = (s + 8.0) / (3.1415 * 8.0);
    return pow(max(dot(reflect(e, n), l), 0.0), s) * nrm;
}

vec3 sea_color(vec3 p, vec3 n, vec3 eye, vec3 dist) {
    float fresnel_o = 1.0 - max(dot(n, -eye), 0.0);
    float fresnel = pow(fresnel_o, 3.0) * 0.65;

    vec3 refl = sky_color(reflect(eye, n));

    vec3 ret = SEA_BASE;
    ret = mix(ret, refl, fresnel);

    float atten = max(1.0 - dot(dist, dist) * 0.001, 0.0);
    ret += SEA_WATER_COLOR * (p.y - uSeaHeight) * 0.18 * atten;

    return ret;
}

vec3 getSunColor(vec3 sunDir) {
    float sunHeight = sunDir.y;
    return mix(vec3(1.4, 0.5, 0.15), vec3(1.0, 0.95, 0.9), smoothstep(-0.05, 0.3, sunHeight));
}

// Compute wave gradient for Jacobian-based caustics
vec2 getWaveGradient(vec2 xz, float eps) {
    float h0 = map_detailed(vec3(xz.x, 0.0, xz.y));
    float hx = map_detailed(vec3(xz.x + eps, 0.0, xz.y));
    float hz = map_detailed(vec3(xz.x, 0.0, xz.y + eps));
    return vec2(hx - h0, hz - h0) / eps;
}

// Raw Jacobian determinant (shared by foam and caustics)
float getJacobianDet(vec2 xz, float scale) {
    float eps = 0.2;
    vec2 pos = xz * scale;

    vec2 grad = getWaveGradient(pos, eps);
    float n_ratio = 1.0 / 1.33;
    vec2 grad_dx = (getWaveGradient(pos + vec2(eps, 0.0), eps) - grad) / eps;
    vec2 grad_dz = (getWaveGradient(pos + vec2(0.0, eps), eps) - grad) / eps;

    float j11 = 1.0 + n_ratio * grad_dx.x;
    float j12 = n_ratio * grad_dz.x;
    float j21 = n_ratio * grad_dx.y;
    float j22 = 1.0 + n_ratio * grad_dz.y;

    return abs(j11 * j22 - j12 * j21);
}

// Jacobian-based caustic pattern for underwater surface
float getUnderwaterCaustic(vec2 xz, float scale) {
    float det = getJacobianDet(xz, scale);
    return clamp(1.0 / (det + 0.001) - 0.8, 0.0, 2.0);
}

void main() {
    vec3 p = vWorldPosition;
    vec3 dist = p - cameraPosition;
    vec3 eye = normalize(dist);

    float eps = dot(dist, dist) * 0.0001;
    eps = max(eps, 0.001);

    vec3 n = getNormal(p, eps);

    if (gl_FrontFacing) {
        // Step 3: Detail normal map blend (before any lighting)
        vec2 worldUV = p.xz * 0.01;
        vec3 n1 = texture2D(uNormalMap, worldUV * 10.0 + uTime * vec2(0.02, 0.01)).xyz * 2.0 - 1.0;
        vec3 n2 = texture2D(uNormalMap, worldUV * 17.0 + uTime * vec2(-0.015, 0.02)).xyz * 2.0 - 1.0;
        vec3 detailN = normalize(vec3(n1.xy + n2.xy, n1.z));
        n = normalize(vec3(n.x + detailN.x * uNormalStrength, n.y, n.z + detailN.y * uNormalStrength));

        // Existing: sea_color with Fresnel (uses blended normal)
        vec3 color = sea_color(p, n, eye, dist);

        // Existing: diffuse + specular
        vec3 light = normalize(uSunDirection);
        color += vec3(diffuse(n, light, 80.0) * SEA_WATER_COLOR) * 0.12;
        color += vec3(specular(n, light, eye, 60.0));

        // Step 4: SSS wave crest glow
        vec3 sunColor = getSunColor(light);
        vec3 H = normalize(n + light);
        float VdotH = pow(clamp(dot(-eye, -H), 0.0, 1.0), 4.0);
        float heightFactor = max(0.0, p.y) * uSSSIntensity;
        color += SEA_WATER_COLOR * VdotH * heightFactor * sunColor;

        // Step 5: Jacobian foam
        float jacobian = getJacobianDet(p.xz, 1.0);
        float foam = smoothstep(0.3, 0.0, jacobian) * uFoamAmount;
        color = mix(color, vec3(0.95, 0.97, 1.0), foam * 0.6);

        // Step 2: Horizon haze (after all surface color, before gamma)
        float hazeDist = length(dist);
        float fogAmount = 1.0 - exp(-hazeDist * 0.00015);
        vec3 fogColor = mix(vec3(0.55, 0.6, 0.7), sunColor * 0.3, 0.3);
        color = mix(color, fogColor, fogAmount);

        // Gamma correction
        color = pow(color, vec3(0.75));

        gl_FragColor = vec4(color, 1.0);
    } else {
        // Underwater -- looking up at the surface from below
        vec3 flippedNormal = -n;
        vec3 sunDir = normalize(uSunDirection);

        // Depth below surface
        float depth = max(0.0, -cameraPosition.y);

        // View direction from underwater (looking up)
        vec3 viewDir = -eye;

        // === Snell's Window ===
        float criticalAngle = asin(1.0 / 1.33);
        float cosNV = dot(flippedNormal, viewDir);
        float viewAngle = acos(clamp(cosNV, 0.0, 1.0));

        float snellWindow = 1.0 - smoothstep(criticalAngle - 0.1, criticalAngle + 0.05, viewAngle);

        float eta = 1.33;
        vec3 refracted = refract(-viewDir, flippedNormal, 1.0 / eta);
        vec3 skyThroughWindow = sky_color(refracted) * 1.2;

        float sunThroughWindow = pow(max(dot(refracted, sunDir), 0.0), 64.0) * 2.0;

        // === Surface Caustic Patterns (Jacobian) ===
        float caustic1 = getUnderwaterCaustic(p.xz, 1.0);
        float caustic2 = getUnderwaterCaustic(p.xz, 0.6);
        float caustic = (caustic1 + caustic2 * 0.4) * 0.5;

        // === Wavelength-dependent absorption ===
        vec3 absorption = exp(-depth * vec3(0.4, 0.15, 0.06));

        // === Depth-based base color ===
        float depthFactor = exp(-depth * 0.3);
        vec3 deepColor = vec3(0.0, 0.02, 0.08);
        vec3 shallowColor = vec3(0.0, 0.15, 0.25);
        vec3 baseColor = mix(deepColor, shallowColor, depthFactor);

        // === Total internal reflection color ===
        vec3 tirColor = baseColor * 0.8;

        // === Combine ===
        vec3 color = baseColor;

        color = mix(tirColor, skyThroughWindow * absorption, snellWindow);
        color += sunThroughWindow * snellWindow * absorption;

        float sunIntensity = max(sunDir.y, 0.0);
        color += caustic * vec3(0.15, 0.3, 0.35) * depthFactor * sunIntensity;

        float lightPen = pow(max(dot(flippedNormal, sunDir), 0.0), 4.0);
        color += lightPen * vec3(0.05, 0.15, 0.2) * depthFactor * absorption;

        color = pow(color, vec3(0.75));

        gl_FragColor = vec4(color, 1.0);
    }
}
`;

function ProceduralOcean({ sunPosition, seaSpeed = 0.8, seaHeight = 0.6, seaChoppy = 2.0, seaFreq = 0.16, normalStrength = 0.4, sssIntensity = 1.2, foamAmount = 0.6 }) {
  const ref = useRef();

  const geom = useMemo(() => new THREE.PlaneGeometry(10000, 10000, 256, 256), []);

  const normalMap = useMemo(() => generateWaterNormalMap(512), []);

  const uniforms = useMemo(() => ({
    uTime: { value: 0 },
    uSunDirection: { value: new THREE.Vector3(0, 1, 0.8) },
    uSeaSpeed: { value: seaSpeed },
    uSeaHeight: { value: seaHeight },
    uSeaChoppy: { value: seaChoppy },
    uSeaFreq: { value: seaFreq },
    uNormalMap: { value: normalMap },
    uNormalStrength: { value: normalStrength },
    uSSSIntensity: { value: sssIntensity },
    uFoamAmount: { value: foamAmount },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  useFrame((state, delta) => {
    const mat = ref.current.material;
    mat.uniforms.uTime.value += delta;
    mat.uniforms.uSeaSpeed.value = seaSpeed;
    mat.uniforms.uSeaHeight.value = seaHeight;
    mat.uniforms.uSeaChoppy.value = seaChoppy;
    mat.uniforms.uSeaFreq.value = seaFreq;
    mat.uniforms.uNormalStrength.value = normalStrength;
    mat.uniforms.uSSSIntensity.value = sssIntensity;
    mat.uniforms.uFoamAmount.value = foamAmount;
    if (sunPosition) {
      mat.uniforms.uSunDirection.value.set(...sunPosition).normalize();
    }
  });

  return (
    <mesh ref={ref} geometry={geom} rotation-x={-Math.PI / 2}>
      <shaderMaterial
        uniforms={uniforms}
        vertexShader={oceanVertexShader}
        fragmentShader={oceanFragmentShader}
        side={THREE.DoubleSide}
        toneMapped={false}
      />
    </mesh>
  );
}

export default ProceduralOcean;
